import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Play, Square, X } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog } from '../../lib/dialogs'
import Modal from '../ui/Modal'
import { useTimer, type ActiveTimer } from '../../contexts/TimerContext'
import { useAuth } from '../../contexts/AuthContext'
import type { CreateTimeEntryInput } from '@jr2os/shared'

/**
 * #8 — Pill flotante con el cronómetro activo.
 *
 * Aparece en la esquina inferior derecha mientras haya un timer activo en
 * el `TimerContext`. Muestra el código de la oportunidad y el contador
 * HH:MM:SS actualizado cada segundo. Stop abre un modal de descripción que,
 * al confirmar, hace `POST /time-entries` con `startedAt`, `endedAt` y
 * `durationMinutes` calculados.
 */
export default function TimerFloating() {
  const { t } = useTranslation(['time', 'common'])
  const { active, stop, cancel } = useTimer()
  const navigate = useNavigate()
  const [now, setNow] = useState(() => Date.now())
  const [showStopModal, setShowStopModal] = useState<ActiveTimer | null>(null)

  // Tick cada segundo solo si hay timer activo.
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [active])

  const onStopClick = useCallback(() => {
    // Snapshot del timer antes de pararlo, para el modal.
    if (!active) return
    setShowStopModal(active)
    stop()
  }, [active, stop])

  const onCancelClick = useCallback(async () => {
    if (!(await confirmDialog({ message: t('floating.confirmDiscard'), tone: 'danger' }))) return
    cancel()
  }, [cancel])

  // Si abrimos el modal y luego cerramos sin guardar, no se ha persistido
  // ningún TimeEntry; el snapshot se descarta. Eso es intencionado.

  if (!active && !showStopModal) return null

  const elapsedSec = active
    ? Math.max(0, Math.floor((now - new Date(active.startedAt).getTime()) / 1000))
    : 0

  return (
    <>
      {active && (
        <div className="fixed bottom-4 right-4 z-40 surface-deep px-3 py-2 rounded-full shadow-lg border border-jr2-500/40 flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/opportunities/${active.opportunityId}`)}
            className="flex items-center gap-2 t-primary text-sm hover:text-jr2-700 dark:hover:text-jr2-200"
            title={t('floating.goToOpportunity')}
          >
            <Play size={12} className="text-emerald-400 animate-pulse" />
            <span className="font-mono text-xs text-jr2-700 dark:text-jr2-300">{active.opportunityCode}</span>
            <span className="font-mono text-sm">{formatSeconds(elapsedSec)}</span>
          </button>
          <button
            type="button"
            onClick={onStopClick}
            className="w-7 h-7 grid place-items-center rounded-full hover:bg-jr2-500/20 t-secondary hover:text-jr2-700 dark:hover:text-jr2-200"
            title={t('floating.stopAndSave')}
          >
            <Square size={11} />
          </button>
          <button
            type="button"
            onClick={onCancelClick}
            className="w-7 h-7 grid place-items-center rounded-full hover:bg-rose-500/15 t-faint hover:text-rose-700 dark:hover:text-rose-300"
            title={t('floating.discard')}
          >
            <X size={12} />
          </button>
        </div>
      )}

      {showStopModal && (
        <StopTimerModal
          snapshot={showStopModal}
          onClose={() => setShowStopModal(null)}
          onSaved={() => setShowStopModal(null)}
        />
      )}
    </>
  )
}

function StopTimerModal({
  snapshot,
  onClose,
  onSaved,
}: {
  snapshot: ActiveTimer
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation(['time', 'common'])
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const auth = useAuth()
  const currentUserId = auth.user?.id ?? null
  const [myRoles, setMyRoles] = useState<
    Array<{ projectRoleId: string; projectRoleName: string }>
  >([])
  const [projectRoleId, setProjectRoleId] = useState<string>('')
  const [loadingRoles, setLoadingRoles] = useState(true)

  useEffect(() => {
    if (!currentUserId) {
      setLoadingRoles(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const { opportunity } = await api.get<{
          opportunity: {
            assignments: Array<{
              userId: string
              projectRoleId: string
              projectRoleName: string
            }>
          }
        }>(`/opportunities/${snapshot.opportunityId}`)
        if (cancelled) return
        const mine = opportunity.assignments
          .filter((a) => a.userId === currentUserId)
          .map((a) => ({ projectRoleId: a.projectRoleId, projectRoleName: a.projectRoleName }))
        setMyRoles(mine)
        if (mine.length === 1) setProjectRoleId(mine[0]!.projectRoleId)
      } catch {
        if (!cancelled) setMyRoles([])
      } finally {
        if (!cancelled) setLoadingRoles(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [snapshot.opportunityId, currentUserId])

  // endedAt CONGELADO al abrir el modal: si lo recalculamos en cada render,
  // cada keystroke del input de descripción rerenderiza y la duración crece.
  const [endedAt] = useState(() => new Date())
  const startedAt = new Date(snapshot.startedAt)
  const elapsedSec = Math.max(0, Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000))
  const durationMinutes = Math.max(1, Math.round(elapsedSec / 60))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!description.trim()) {
      setError(t('stopModal.errorDescription'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (myRoles.length > 1 && !projectRoleId) {
        setError(t('stopModal.errorRole'))
        setBusy(false)
        return
      }
      const payload: CreateTimeEntryInput = {
        opportunityId: snapshot.opportunityId,
        description: description.trim(),
        performedOn: startedAt.toISOString(),
        durationMinutes,
        startedAt: startedAt.toISOString(),
        endedAt: endedAt.toISOString(),
        phase: snapshot.phase,
        ...(projectRoleId ? { projectRoleId } : {}),
        nonBillable: false,
      }
      await api.post('/time-entries', payload)
      onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('common:error')))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-jr2-500/50'

  return (
    <Modal open onClose={onClose} title={`${t('stopModal.title')} · ${snapshot.opportunityCode}`}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="text-xs t-muted">
          {t('stopModal.timedDuration')}:{' '}
          <span className="font-mono t-primary">{formatSeconds(elapsedSec)}</span>{' '}
          <span className="t-faint">
            ({durationMinutes} min · {snapshot.phase === 'PREVENTA' ? t('phase.preventa') : t('phase.ejecucion')})
          </span>
        </div>
        <label className="block">
          <span className="text-xs t-muted">{t('stopModal.descriptionLabel')}</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('stopModal.descriptionPlaceholder')}
            className={input}
            required
            autoFocus
          />
        </label>

        {myRoles.length > 1 && (
          <label className="block">
            <span className="text-xs t-muted">{t('stopModal.projectRoleLabel')}</span>
            <select
              value={projectRoleId}
              onChange={(e) => setProjectRoleId(e.target.value)}
              className={input}
              required
            >
              <option value="">{t('stopModal.chooseRole')}</option>
              {myRoles.map((r) => (
                <option key={r.projectRoleId} value={r.projectRoleId}>
                  {r.projectRoleName}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('floating.discard')}
          </button>
          <button
            type="submit"
            disabled={busy || loadingRoles}
            title={loadingRoles ? t('stopModal.loadingRoles') : ''}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {(busy || loadingRoles) && <Loader2 size={14} className="animate-spin" />}
            {t('stopModal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function formatSeconds(secs: number): string {
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
